import { useEffect, useState } from "react";
import { Warning, ShieldStar, ArrowRight } from "@phosphor-icons/react";
import API from "../lib/api";

/**
 * Inline warning banner that appears inside Assign-Supplier dialogs the moment an
 * admin selects a Fair/Slow-graded vendor, if there's a higher-graded alternative
 * covering the same area + trade.
 *
 * Props:
 *   candidateId   - the supplier user_id or vendor.id about to be assigned
 *   entityType    - "job" | "emergency_job" | "work_order" | "task"
 *   entityId      - id of the entity being assigned
 *   projectId     - optional, used as a fallback for area/trade inheritance
 *   trade         - optional explicit trade
 *   area          - optional explicit area (town/county)
 *   onAcceptSuggestion(suggestion) - fired when user clicks "Use suggested instead"
 *
 * The component is silent when the candidate is already Good/Excellent or no
 * better alternative exists — zero visual noise on happy paths.
 */
export default function ReassignmentWarning({
  candidateId,
  entityType,
  entityId,
  projectId,
  trade,
  area,
  onAcceptSuggestion,
  "data-testid": testId = "reassignment-warning",
}) {
  const [data, setData] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
    if (!candidateId) { setData(null); return; }
    let cancelled = false;
    const params = { candidate_vendor_id: candidateId };
    if (entityType) params.entity_type = entityType;
    if (entityId) params.entity_id = entityId;
    if (projectId) params.project_id = projectId;
    if (trade) params.trade = trade;
    if (area) params.area = area;
    API.get("/vendor-reassignments/preview", { params })
      .then((r) => { if (!cancelled) setData(r.data || null); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, [candidateId, entityType, entityId, projectId, trade, area]);

  if (!data || !data.warn || dismissed) return null;
  const cand = data.candidate || {};
  const sug = data.suggestion || {};
  const toneClasses = cand.sla_grade === "Slow"
    ? "border-rose-500/40 bg-rose-500/10 text-rose-900 dark:text-rose-200"
    : "border-amber-500/40 bg-amber-500/10 text-amber-900 dark:text-amber-200";

  return (
    <div className={`flex items-start gap-2 rounded-md border px-3 py-2.5 text-xs ${toneClasses}`} data-testid={testId}>
      <Warning size={16} weight="fill" className="mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <p className="font-semibold">
          {cand.company_name || "This supplier"} is graded {cand.sla_grade}
          {typeof cand.sla_avg_response_seconds === "number" && cand.sla_avg_response_seconds > 0
            ? ` · avg response ${_fmtSeconds(cand.sla_avg_response_seconds)}`
            : ""}
        </p>
        <p className="opacity-90">
          <ShieldStar size={11} weight="fill" className="inline -mt-0.5 mr-0.5" />
          <span className="font-semibold">{sug.supplier_name}</span> is rated <b>{sug.grade}</b> and covers the same area
          {typeof sug.avg_response_seconds === "number" && sug.avg_response_seconds > 0
            ? ` (avg ${_fmtSeconds(sug.avg_response_seconds)})`
            : ""}.
        </p>
        {Array.isArray(sug.area_match) && sug.area_match.length > 0 && (
          <p className="text-[10px] opacity-70 capitalize">Shared areas: {sug.area_match.join(", ")}</p>
        )}
        <div className="flex flex-wrap gap-2 pt-1">
          {onAcceptSuggestion && (
            <button
              type="button"
              onClick={() => onAcceptSuggestion(sug)}
              className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md bg-primary text-primary-foreground font-semibold hover:opacity-90"
              data-testid={`${testId}-accept`}
            >
              Use {sug.supplier_name} instead <ArrowRight size={10} weight="bold" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setDismissed(true)}
            className="text-[11px] px-2 py-1 rounded-md border border-current/40 hover:bg-current/5 font-medium"
            data-testid={`${testId}-dismiss`}
          >
            Keep current choice
          </button>
        </div>
      </div>
    </div>
  );
}

function _fmtSeconds(s) {
  if (!s || s < 0) return "";
  if (s < 3600) return `${Math.round(s / 60)} min`;
  if (s < 86400) return `${(s / 3600).toFixed(1)}h`;
  return `${(s / 86400).toFixed(1)}d`;
}
